class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.dispatchEvent(new Event('atelier:cart-open'));

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.dispatchEvent(new Event('atelier:cart-close'));
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Shopify validates discount codes; keep any already applicable codes when adding one.
class AtelierCartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.onSubmit = this.apply.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() { this.form?.removeEventListener('submit', this.onSubmit); }

  async apply(event) {
    event.preventDefault();
    if (this.pending) return;
    const code = this.form.elements.discount.value.trim();
    if (!code) return;
    const drawer = this.closest('cart-drawer');
    const button = this.querySelector('button');
    const status = this.querySelector('[role="status"]');
    this.pending = true;
    button.disabled = true;
    status.textContent = (this.dataset.checking || "Checking\u2026");
    let message;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!currentResponse.ok) throw new Error('Cart unavailable');
      const current = await currentResponse.json();
      const codes = (current.discount_codes || []).filter(item => item.applicable).map(item => item.code);
      if (!codes.some(item => item.toLowerCase() === code.toLowerCase())) codes.push(code);
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: drawer.getSectionsToRender().map(item => item.id), sections_url: window.location.pathname })
      });
      if (!response.ok) throw new Error('Discount unavailable');
      const cart = await response.json();
      const accepted = (cart.discount_codes || []).some(item => item.applicable && item.code.toLowerCase() === code.toLowerCase());
      message = accepted ? (this.dataset.accepted || "Discount code applied.") : (this.dataset.rejected || "This code does not apply to these items.");
      if (cart.sections && Object.values(cart.sections).every(Boolean)) {
        drawer.renderContents(cart);
        const input = drawer.querySelector('#CartDrawer-Discount');
        if (input) { input.value = accepted ? '' : code; input.focus(); }
      } else {
        message = (this.dataset.refresh || "Cart updated. Reload the page for the latest total.");
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    } catch (error) {
      message = (this.dataset.error || "Unable to check the code. Please try again.");
    } finally {
      this.pending = false;
      button.disabled = false;
      const liveStatus = drawer.querySelector('#CartDrawer-DiscountStatus');
      if (liveStatus) liveStatus.textContent = message;
    }
  }
}
customElements.define('atelier-cart-discount', AtelierCartDiscount);

class AtelierCartTimer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    this.onOpen = () => this.start();
    this.onClose = () => clearInterval(this.timer);
    this.drawer?.addEventListener('atelier:cart-open', this.onOpen);
    this.drawer?.addEventListener('atelier:cart-close', this.onClose);
    if (this.drawer?.classList.contains('active')) this.start();
  }
  start() {
    clearInterval(this.timer);
    const duration = Number(this.dataset.minutes) * 60000;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const key = 'atelier-cart-checkout-deadline-' + this.dataset.minutes;
    let end;
    try {
      end = Number(sessionStorage.getItem(key));
      if (!end) { end = Date.now() + duration; sessionStorage.setItem(key, String(end)); }
    } catch { end = this.deadline || Date.now() + duration; }
    this.deadline = end;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      this.hidden = remaining === 0;
      if (!remaining) { clearInterval(this.timer); return; }
      if (!document.hidden) this.querySelector('time').textContent = `${Math.floor(remaining / 60).toString().padStart(2, '0')}:${(remaining % 60).toString().padStart(2, '0')}`;
    };
    this.timer = setInterval(tick, 1000);
    tick();
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.drawer?.removeEventListener('atelier:cart-open', this.onOpen);
    this.drawer?.removeEventListener('atelier:cart-close', this.onClose);
  }
}
customElements.define('atelier-cart-timer', AtelierCartTimer);
